import { Set } from 'immutable';
import Big from 'big.js';

import { addThousandsSeparator, formatFileSize, toFixed } from '@/utils/number';

import { CURRENCY, CURRENCY_LOCATION, CURRENCY_SIGN } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ATTRIBUTE } from '@/constants/attributes';

export const MAX_NUMBER_OF_PHOTOS = {
  [EBAY]: 24,
  [ETSY]: 20,
  [FAIRE]: 250,
  [SHOPIFY]: 255,
};

export const MAX_NUMBER_OF_VIDEOS = {
  [EBAY]: 1,
  [ETSY]: 1,
  [SHOPIFY]: 10,
};

export const MAX_PRICE = { // greater than valid value
  [ETSY]: 50000,
  [SHOPIFY]: 1000000000000000000,
};

export const MIN_PRICE = { // less than or equals valid value
  [EBAY]: 0.99,
  [ETSY]: 0.2,
  [SHOPIFY]: 0,
};

export const MIN_INTERNATIONAL_PRICE = {
  [CURRENCY.AUD]: 0.01,
  [CURRENCY.CAD]: 0.01,
  [CURRENCY.EUR]: 0.01,
  [CURRENCY.GBP]: 0.01,
  [CURRENCY.USD]: 0.01,
};

export const MAX_INTERNATIONAL_PRICE = {
  [CURRENCY.AUD]: 20000,
  [CURRENCY.CAD]: 29000,
  [CURRENCY.EUR]: 20000,
  [CURRENCY.GBP]: 16000,
  [CURRENCY.USD]: 32000,
};

export const MIN_RETAIL_WHOLESALE_RATIO = 1.25;
export const MAX_RETAIL_WHOLESALE_RATIO = 10;

export const MAX_QUANTITY = { // greater than valid value
  [ETSY]: 1000,
  [SHOPIFY]: 1000000001,
};

export const MIN_QUANTITY = { // less than or equals valid value
  [EBAY]: 0,
  [ETSY]: 0,
  [SHOPIFY]: -1000000000,
};

export const MAX_SKU_LENGTH = {
  [EBAY]: 50,
  [ETSY]: 32,
  [SHOPIFY]: 255,
};

export const MAX_BARCODE_LENGTH = {
  [SHOPIFY]: 255,
};

export const MAX_TAG_LENGTH = {
  [ETSY]: 20,
  [SHOPIFY]: 255,
};

export const MAX_NUMBER_OF_TAGS = {
  [ETSY]: 13,
  [SHOPIFY]: 250,
};

export const MAX_NUMBER_OF_AI_TAGS = {
  [ETSY]: 13,
  [SHOPIFY]: 25,
};

export const MAX_NUMBER_OF_MATERIALS = {
  [ETSY]: 13,
};

export const MAX_MATERIAL_LENGTH = {
  [ETSY]: 45,
};

export const MAX_NUMBER_OF_SECTIONS = {
  [ETSY]: 20,
};

export const MAX_NUMBER_OF_FILES = {
  [ETSY]: 5,
};

export const MAX_PRODUCT_TYPE_LENGTH = 255;

export const MAX_VENDOR_LENGTH = 255;

export const MAX_SECTION_LENGTH = 24;

export const MAX_WEIGHT = {
  [ETSY]: 1000000000000,
  [FAIRE]: 1000000,
  [SHOPIFY]: 2000000000,
};

export const MIN_WEIGHT = {
  [ETSY]: 0,
  [FAIRE]: 0,
  [SHOPIFY]: 0,
};

export const MAX_HEIGHT = {
  [ETSY]: 1000000000000,
  [FAIRE]: 1000000,
};

export const MIN_HEIGHT = {
  [ETSY]: 0,
  [FAIRE]: 0,
};

export const MAX_LENGTH = {
  [ETSY]: 1000000000000,
  [FAIRE]: 1000000,
};

export const MIN_LENGTH = {
  [ETSY]: 0,
  [FAIRE]: 0,
};

export const MAX_WIDTH = {
  [ETSY]: 1000000000000,
  [FAIRE]: 1000000,
};

export const MIN_WIDTH = {
  [ETSY]: 0,
  [FAIRE]: 0,
};

export const MAX_TITLE_LENGTH = {
  [EBAY]: 80,
  [ETSY]: 140,
  [FAIRE]: 60,
  [SHOPIFY]: 255,
};

export const MAX_SEO_TITLE_LENGTH = {
  [SHOPIFY]: 70,
};

export const MAX_SEO_DESCRIPTION_LENGTH = {
  [SHOPIFY]: 320,
};

export const MAX_FILE_NAME_LENGTH = {
  [ETSY]: 70,
};

export const MAX_DESCRIPTION_LENGTH = {
  [EBAY]: 500000,
  [ETSY]: 102400,
  [FAIRE]: 3000,
};

export const MAX_CONDITION_DESCRIPTION_LENGTH = {
  [EBAY]: 1000,
};

export const PHOTO_DIMENSIONS_MINIMUM = {
  [EBAY]: 500,
  [ETSY]: 50,
  [FAIRE]: 500,
  [SHOPIFY]: 50,
};

export const PHOTO_DIMENSIONS_MAXIMUM = {
  [EBAY]: 9000,
  [ETSY]: 10000,
  [FAIRE]: 12000,
};

export const PHOTO_DIMENSIONS_RECOMMENDED = {
  [EBAY]: 2000,
  [ETSY]: 2000,
  [FAIRE]: 2000,
  [SHOPIFY]: 2000,
};

export const MAX_PHOTO_SIZE = {
  [EBAY]: 12582912,
  [FAIRE]: 52428800,
  [SHOPIFY]: 20971520,
};

export const MAX_ALT_TEXT_LENGTH = {
  [ETSY]: 500,
  [SHOPIFY]: 512,
};

export const IMAGE_FILE_FORMATS = {
  [EBAY]: Set(['gif', 'jpeg', 'jpg', 'png', 'heic', 'webp', 'tiff']),
  [ETSY]: Set(['gif', 'jpeg', 'jpg', 'png']),
  [FAIRE]: Set(['jpeg', 'jpg', 'png', 'webp']),
  [SHOPIFY]: Set(['gif', 'jpeg', 'jpg', 'png', 'heic', 'webp', 'tiff']),
};

export const VIDEO_FILE_FORMATS = {
  [EBAY]: Set(['mp4', 'quicktime']),
  [ETSY]: Set(['mp4', 'quicktime']),
  [SHOPIFY]: Set(['mp4', 'quicktime']),
};

export const VIDEO_DURATION_MINIMUM = {
  [ETSY]: 5,
  [SHOPIFY]: 0.25,
};

export const VIDEO_DURATION_MAX = {
  [EBAY]: 60,
  [ETSY]: 60,
  [SHOPIFY]: 600,
};

export const MAX_VIDEO_SIZE = {
  [EBAY]: 157286400,
  [ETSY]: 104857600,
  [SHOPIFY]: 1073741824,
};

export const MAX_VIDEO_SIZE_FORMATTED = {
  [EBAY]: formatFileSize(MAX_VIDEO_SIZE[EBAY]),
  [ETSY]: formatFileSize(MAX_VIDEO_SIZE[ETSY]),
  [SHOPIFY]: formatFileSize(MAX_VIDEO_SIZE[SHOPIFY]),
};

export const VIDEO_DIMENSIONS_MINIMUM = {
  [EBAY]: 240,
  [ETSY]: 500,
  [SHOPIFY]: 240,
};

export const VIDEO_DIMENSIONS_MAXIMUM = {
  [EBAY]: {
    WIDTH: 1920,
    HEIGHT: 1920,
  },
  [ETSY]: {
    WIDTH: 3840,
    HEIGHT: 3840,
  },
  [SHOPIFY]: {
    WIDTH: 4096,
    HEIGHT: 4096,
  },
};

export const MAX_FILE_SIZE = {
  [ETSY]: 20971520,
};

export const PERSONALIZATION_CHARACTERS_COUNT_MIN = {
  [ETSY]: 1,
};

export const PERSONALIZATION_CHARACTERS_COUNT_MAX = {
  [ETSY]: 1024,
};

export const PERSONALIZATION_INSTRUCTION_LENGTH_MAX = {
  [ETSY]: 256,
};

export const PERSONALIZATION_QUESTION_LABEL_LENGTH_MAX = {
  [ETSY]: 45,
};

export const PERSONALIZATION_QUESTION_INSTRUCTIONS_LENGTH_MAX = {
  [ETSY]: 120,
};

export const PERSONALIZATION_QUESTION_CHARACTER_LIMIT_MAX = {
  [ETSY]: 1024,
};

export const PERSONALIZATION_QUESTION_FILE_NAME_LENGTH_MAX = {
  [ETSY]: 45,
};

export const PERSONALIZATION_QUESTION_OPTION_NAME_LENGTH_MAX = {
  [ETSY]: 20,
};

export const PERSONALIZATION_QUESTION_OPTIONS_COUNT_MAX = {
  [ETSY]: 30,
};

export const TAXONOMY_LIMITS = {
  47626759760: 6,
  570246213556: 3,
};

export const MAX_LOCATION_LENGTH = {
  [EBAY]: 45,
};

export const MAX_POSTAL_CODE_LENGTH = {
  [EBAY]: 20,
};

function reduceFileFormats(result, value, index, array) {
  const format = `.${value}`;
  return result
    ? index === array.length - 1
      ? `${result} or ${format}`
      : `${result}, ${format}`
    : format;
}

export const ERRORS = {
  ALPHANUMERIC_BEGIN: 'Must begin with alphanumeric character',
  ALPHANUMERIC_CONTAIN: 'Must contain alphanumeric character',
  BARCODE: {
    LENGTH_EXCEEDED: {
      [SHOPIFY]: `Barcode should not exceed ${MAX_BARCODE_LENGTH[SHOPIFY]} characters`,
    },
  },
  CAP: {
    REQUIRED: 'Add CAP',
    TOO_LOW: {
      [SHOPIFY]: 'Compare at price must be positive number',
    },
    TOO_HIGH: {
      [SHOPIFY]: `Compare at price must be lower than $${addThousandsSeparator(MAX_PRICE[SHOPIFY])}`,
    },
  },
  CPI: {
    TOO_LOW: {
      [SHOPIFY]: 'Cost per item must be positive number',
    },
    TOO_HIGH: {
      [SHOPIFY]: `Cost per item must be lower than $${addThousandsSeparator(MAX_PRICE[SHOPIFY])}`,
    },
  },
  DESCRIPTION: {
    REQUIRED: 'Add description',
    LENGTH_EXCEEDED: {
      [EBAY]: `Description should not exceed ${addThousandsSeparator(MAX_DESCRIPTION_LENGTH[EBAY])} characters`,
      [ETSY]: `Description should not exceed ${addThousandsSeparator(MAX_DESCRIPTION_LENGTH[ETSY])} characters`,
      [FAIRE]: `Description should not exceed ${addThousandsSeparator(MAX_DESCRIPTION_LENGTH[FAIRE])} characters`,
    },
  },
  DETAILS: {
    ABOUT: {
      [ATTRIBUTE.IS_SUPPLY]: 'Choose what is it',
      [ATTRIBUTE.WHEN_MADE]: 'Choose when it was made',
      [ATTRIBUTE.WHO_MADE]: 'Choose who made it',
    },
    CATEGORY: 'Choose category',
    CONDITION_ID: 'Choose condition',
    CONDITION_DESCRIPTION: {
      [EBAY]: `Condition description should not exceed ${MAX_CONDITION_DESCRIPTION_LENGTH[EBAY]} characters`,
    },
    DIGITAL_MADE_TO_ORDER: 'Digital files cannot be included for "Made to Order" listings. Delete the files or update "When was it made?" to sync.',
    PRODUCT_TYPE: `Product type should not exceed ${MAX_PRODUCT_TYPE_LENGTH} characters`,
    PRODUCTION_PARTNERS: {
      REQUIRED: 'Production partner is required for this listing',
    },
    SECTION: {
      CAPITALIZED_WORDS_NUMBER_EXCEEDED: 'Section can contain at most three words with all capitalized letters',
      FORBIDDEN_CHARACTERS: 'Section may not contain any of these characters: ^ `',
      LENGTH_EXCEEDED: `Section should not exceed ${MAX_SECTION_LENGTH} characters`,
      EMOJIS_NOT_ALLOWED: 'Section may not contain emojis',
      SPECIAL_CHARACTERS_NUMBER_EXCEEDED: 'Section can contain characters % : & at most once',
    },
    VENDOR: `Vendor should not exceed ${MAX_VENDOR_LENGTH} characters`,
  },
  FILES: {
    REQUIRED: 'At least one file is required',
    TOO_BIG: {
      [ETSY]: `File must be less than ${formatFileSize(MAX_FILE_SIZE[ETSY])}`,
    },
    NAME_FORBIDDEN_CHARACTERS: {
      [ETSY]: 'File name must contain only letters, numbers, hyphens, underscores or periods',
    },
    NAME_LENGTH_EXCEEDED: {
      [ETSY]: `File name should not exceed ${MAX_FILE_NAME_LENGTH[ETSY]} characters`,
    },
  },
  GTIN: {
    INVALID: 'GTIN must be either 8, 12, 13 or 14 digits',
  },
  INTERNATIONAL_PRICE: {
    RETAIL: {
      REQUIRED: 'Add retail price',
      INVALID: {
        [CURRENCY.AUD]: `${CURRENCY_LOCATION[CURRENCY.AUD]} retail price must be between ${CURRENCY_SIGN[CURRENCY.AUD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.AUD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.AUD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.AUD], 2, true)}`,
        [CURRENCY.CAD]: `${CURRENCY_LOCATION[CURRENCY.CAD]} retail price must be between ${CURRENCY_SIGN[CURRENCY.CAD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.CAD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.CAD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.CAD], 2, true)}`,
        [CURRENCY.EUR]: `${CURRENCY_LOCATION[CURRENCY.EUR]} retail price must be between ${CURRENCY_SIGN[CURRENCY.EUR]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.EUR], 2, true)} and ${CURRENCY_SIGN[CURRENCY.EUR]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.EUR], 2, true)}`,
        [CURRENCY.GBP]: `${CURRENCY_LOCATION[CURRENCY.GBP]} retail price must be between ${CURRENCY_SIGN[CURRENCY.GBP]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.GBP], 2, true)} and ${CURRENCY_SIGN[CURRENCY.GBP]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.GBP], 2, true)}`,
        [CURRENCY.USD]: `${CURRENCY_LOCATION[CURRENCY.USD]} retail price must be between ${CURRENCY_SIGN[CURRENCY.USD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.USD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.USD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.USD], 2, true)}`,
      },
    },
    WHOLESALE: {
      REQUIRED: 'Add wholesale price',
      INVALID: {
        [CURRENCY.AUD]: `${CURRENCY_LOCATION[CURRENCY.AUD]} wholesale price must be between ${CURRENCY_SIGN[CURRENCY.AUD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.AUD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.AUD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.AUD], 2, true)}`,
        [CURRENCY.CAD]: `${CURRENCY_LOCATION[CURRENCY.CAD]} wholesale price must be between ${CURRENCY_SIGN[CURRENCY.CAD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.CAD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.CAD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.CAD], 2, true)}`,
        [CURRENCY.EUR]: `${CURRENCY_LOCATION[CURRENCY.EUR]} wholesale price must be between ${CURRENCY_SIGN[CURRENCY.EUR]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.EUR], 2, true)} and ${CURRENCY_SIGN[CURRENCY.EUR]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.EUR], 2, true)}`,
        [CURRENCY.GBP]: `${CURRENCY_LOCATION[CURRENCY.GBP]} wholesale price must be between ${CURRENCY_SIGN[CURRENCY.GBP]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.GBP], 2, true)} and ${CURRENCY_SIGN[CURRENCY.GBP]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.GBP], 2, true)}`,
        [CURRENCY.USD]: `${CURRENCY_LOCATION[CURRENCY.USD]} wholesale price must be between ${CURRENCY_SIGN[CURRENCY.USD]} ${addThousandsSeparator(MIN_INTERNATIONAL_PRICE[CURRENCY.USD], 2, true)} and ${CURRENCY_SIGN[CURRENCY.USD]} ${addThousandsSeparator(MAX_INTERNATIONAL_PRICE[CURRENCY.USD], 2, true)}`,
      },
    },
    RETAIL_WHOLESALE_RATIO: `Retail price needs to be between ${toFixed(MIN_RETAIL_WHOLESALE_RATIO, 2)} and ${toFixed(MAX_RETAIL_WHOLESALE_RATIO, 0)} times the wholesale price`,
  },
  MATERIALS: {
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'Material can only include spaces, letters and numbers',
    },
    LENGTH_EXCEEDED: {
      [ETSY]: `Material should not exceed ${MAX_MATERIAL_LENGTH[ETSY]} characters`,
    },
  },
  META_DESCRIPTION: {
    LENGTH_EXCEEDED: {
      [SHOPIFY]: `Description should not exceed ${MAX_SEO_DESCRIPTION_LENGTH[SHOPIFY]} characters`,
    },
  },
  NOT_A_NUMBER: 'Must be a number',
  PERSONALIZATION: {
    ALPHANUMERIC_BEGIN: 'Personalization instructions must begin with alphanumeric character',
    CAPITALIZED_WORDS_NUMBER_EXCEEDED: 'Personalization instructions can contain at most one word with all capitalized letters',
    LENGTH_EXCEEDED: {
      [ETSY]: `Personalization instructions should not exceed ${PERSONALIZATION_INSTRUCTION_LENGTH_MAX[ETSY]} characters`,
    },
    LIMIT_OUT_OF_RANGE: {
      [ETSY]: `Character limit must be a whole number between ${PERSONALIZATION_CHARACTERS_COUNT_MIN[ETSY]} and ${PERSONALIZATION_CHARACTERS_COUNT_MAX[ETSY]}`,
    },
  },
  PERSONALIZATION_QUESTIONS: {
    CHARACTER_LIMIT: {
      [ETSY]: `Character limit must be a number between 1 and ${PERSONALIZATION_QUESTION_CHARACTER_LIMIT_MAX[ETSY]}`,
    },
    FILES: {
      ALPHANUMERIC_BEGIN: 'File name must begin with alphanumeric character',
      LENGTH_EXCEEDED: {
        [ETSY]: `File name should not exceed ${PERSONALIZATION_QUESTION_FILE_NAME_LENGTH_MAX[ETSY]} characters`,
      },
      MAXIMUM: 'The number of file names must not exceed the number of files',
      MINIMUM: 'Add names for all files',
      NAME_EXIST: 'You may not have two files with same name',
      NO_NAME: 'Fill in file name',
      ONE_PER_LISTING: 'Personalization can contain at most one file upload per listing',
    },
    INSTRUCTIONS: {
      ALPHANUMERIC_BEGIN: 'Instructions must begin with alphanumeric character',
      CAPITALIZED_WORDS: 'Instructions can contain at most one word with all capitalized letters',
      LENGTH_EXCEEDED: {
        [ETSY]: `Instructions should not exceed ${PERSONALIZATION_QUESTION_INSTRUCTIONS_LENGTH_MAX[ETSY]} characters`,
      },
    },
    LABEL: {
      ALPHANUMERIC_BEGIN: 'Field title must begin with alphanumeric character',
      CAPITALIZED_WORDS: 'Field title can contain at most one word with all capitalized letters',
      EMPTY: 'Fill in field title',
      LENGTH_EXCEEDED: {
        [ETSY]: `Field title should not exceed ${PERSONALIZATION_QUESTION_LABEL_LENGTH_MAX[ETSY]} characters`,
      },
    },
    OPTIONS: {
      NAME_EXIST: 'You may not have two options with same name',
      NO_NAME: 'Fill in option',
      NO_OPTIONS: 'Add at least one option',
      LENGTH_EXCEEDED: {
        [ETSY]: `Option name should not exceed ${PERSONALIZATION_QUESTION_OPTION_NAME_LENGTH_MAX[ETSY]} characters`,
      },
      NUMBER_EXCEEDED: {
        [ETSY]: `Options number should not exceed ${PERSONALIZATION_QUESTION_OPTIONS_COUNT_MAX[ETSY]}`,
      },
    },
  },
  PAGE_TITLE: {
    LENGTH_EXCEEDED: {
      [SHOPIFY]: `Title should not exceed ${MAX_SEO_TITLE_LENGTH[SHOPIFY]} characters`,
    },
  },
  PHOTOS: {
    ALT_TEXT_MISSING: 'Missing alt text',
    ALT_TEXT_TOO_LONG: {
      [ETSY]: `Alt text should not exceed ${MAX_ALT_TEXT_LENGTH[ETSY]} characters`,
      [SHOPIFY]: `Alt text should not exceed ${MAX_ALT_TEXT_LENGTH[SHOPIFY]} characters`,
    },
    FILE_TOO_BIG: {
      [EBAY]: `Image file must be less than ${formatFileSize(MAX_PHOTO_SIZE[EBAY])}`,
      [FAIRE]: `Image file must be less than ${formatFileSize(MAX_PHOTO_SIZE[FAIRE])}`,
      [SHOPIFY]: `Image file must be less than ${formatFileSize(MAX_PHOTO_SIZE[SHOPIFY])}`,
    },
    LENGTH_EXCEEDED: 'Maximum number of photos reached',
    LOW_RESOLUTION: 'Low-resolution photo',
    REQUIRED: 'Add photo',
    TOO_BIG: {
      [EBAY]: `Images must be at most ${addThousandsSeparator(PHOTO_DIMENSIONS_MAXIMUM[EBAY])} pixels in each dimension`,
      [ETSY]: `Images must be at most ${addThousandsSeparator(PHOTO_DIMENSIONS_MAXIMUM[ETSY])} pixels in each dimension`,
      [FAIRE]: `Images must be at most ${addThousandsSeparator(PHOTO_DIMENSIONS_MAXIMUM[FAIRE])} pixels in each dimension`,
    },
    TOO_SMALL: {
      [EBAY]: `Images must be at least ${PHOTO_DIMENSIONS_MINIMUM[EBAY]} pixels in each dimension`,
      [ETSY]: `Images must be at least ${PHOTO_DIMENSIONS_MINIMUM[ETSY]} pixels in each dimension`,
      [FAIRE]: `Images must be at least ${PHOTO_DIMENSIONS_MINIMUM[FAIRE]} pixels in each dimension`,
      [SHOPIFY]: `Images must be at least ${PHOTO_DIMENSIONS_MINIMUM[SHOPIFY]} pixels in each dimension`,
    },
    UNKNOWN: 'Image file is corrupted or unsupported',
    UNSUPPORTED_FORMAT: {
      [EBAY]: `File must be one of the following types: ${IMAGE_FILE_FORMATS[EBAY].reduce(reduceFileFormats, '')}`,
      [ETSY]: `File must be one of the following types: ${IMAGE_FILE_FORMATS[ETSY].reduce(reduceFileFormats, '')}`,
      [FAIRE]: `File must be one of the following types: ${IMAGE_FILE_FORMATS[FAIRE].reduce(reduceFileFormats, '')}`,
      [SHOPIFY]: `File must be one of the following types: ${IMAGE_FILE_FORMATS[SHOPIFY].reduce(reduceFileFormats, '')}`,
    },
  },
  PRICE: {
    REQUIRED: 'Add price',
    TOO_LOW: {
      [EBAY]: `Price must be at least $${addThousandsSeparator(MIN_PRICE[EBAY], 2, true)}`,
      [ETSY]: `Price must be at least $${addThousandsSeparator(MIN_PRICE[ETSY], 2, true)}`,
      [SHOPIFY]: `Price must be at least $${addThousandsSeparator(MIN_PRICE[SHOPIFY], 2, true)}`,
    },
    TOO_HIGH: {
      [ETSY]: `Price must be lower than $${addThousandsSeparator(MAX_PRICE[ETSY])}`,
      [SHOPIFY]: `Price must be lower than $${addThousandsSeparator(MAX_PRICE[SHOPIFY])}`,
    },
  },
  QUANTITY: {
    REQUIRED: 'Add quantity',
    INVALID: 'Quantity must be a whole number',
    MUST_BE_IN_STOCK: 'At least one must be in stock',
    TOO_LOW: {
      [EBAY]: `Quantity must be at least ${addThousandsSeparator(MIN_QUANTITY[EBAY])}`,
      [ETSY]: `Quantity must be at least ${addThousandsSeparator(MIN_QUANTITY[ETSY])}`,
      [SHOPIFY]: `Quantity must at least ${addThousandsSeparator(MIN_QUANTITY[SHOPIFY])}`,
    },
    TOO_HIGH: {
      [ETSY]: `Quantity must be lower than ${addThousandsSeparator(MAX_QUANTITY[ETSY])}`,
      [SHOPIFY]: `Quantity must be lower than ${addThousandsSeparator(MAX_QUANTITY[SHOPIFY])}`,
    },
  },
  REQUIRED: 'Required',
  SHIPPING: {
    [EBAY]: {
      COUNTRY: 'Choose country',
      PAYMENT_POLICY: 'Choose payment policy',
      RETURN_POLICY: 'Choose return policy',
      SHIPPING_POLICY: 'Choose shipping policy',
      DEPTH: {
        IS_NEGATIVE: 'Depth must be positive number',
      },
      LENGTH: {
        IS_NEGATIVE: 'Length must be positive number',
      },
      LOCATION: {
        LENGTH_EXCEEDED: `Location should not exceed ${MAX_LOCATION_LENGTH[EBAY]} characters`,
        REQUIRED: 'Location is required',
      },
      POSTAL_CODE: {
        LENGTH_EXCEEDED: `Postal code should not exceed ${MAX_POSTAL_CODE_LENGTH[EBAY]} characters`,
        REQUIRED: 'Postal code is required',
      },
      WEIGHT: {
        IS_NEGATIVE: 'Weight must be positive number',
        REQUIRED: 'Weight is required',
      },
      WIDTH: {
        IS_NEGATIVE: 'Width must be positive number',
      },
    },
    [ETSY]: {
      PROCESSING_PROFILE: 'Choose processing profile',
      TEMPLATE: 'Choose shipping profile',
      HEIGHT: {
        REQUIRED: 'Calculated shipping requires item length, width, height',
        TOO_HIGH: `Height must be lower than ${addThousandsSeparator(MAX_HEIGHT[ETSY])}`,
        TOO_LOW: 'Height must be positive number',
      },
      LENGTH: {
        NOT_LARGEST: 'Length should be the largest dimension',
        REQUIRED: 'Calculated shipping requires item length, width, height',
        TOO_HIGH: `Length must be lower than ${addThousandsSeparator(MAX_LENGTH[ETSY])}`,
        TOO_LOW: 'Length must be positive number',
      },
      WIDTH: {
        REQUIRED: 'Calculated shipping requires item length, width, height',
        TOO_HIGH: `Width must be lower than ${addThousandsSeparator(MAX_WIDTH[ETSY])}`,
        TOO_LOW: 'Width must be positive number',
      },
      WEIGHT: {
        REQUIRED: 'Calculated shipping requires item weight',
        TOO_HIGH: `Weight must be lower than ${addThousandsSeparator(new Big(MAX_WEIGHT[ETSY]).div(16))}`,
        TOO_LOW: 'Weight must be positive number',
      },
      RETURN_POLICY: 'Choose return policy',
    },
    [FAIRE]: {
      CASE_SIZE: {
        NON_POSITIVE: 'Case size must be positive number',
        REQUIRED: 'Case size is required',
      },
      MINIMUM_ORDER_QUANTITY: {
        NON_POSITIVE: 'Minimum order quantity must be at least 1',
        REQUIRED: 'Minimum order quantity is required',
      },
      HEIGHT: {
        TOO_HIGH: `Height must be at most ${addThousandsSeparator(MAX_HEIGHT[FAIRE])}`,
        TOO_LOW: `Height must be at least ${addThousandsSeparator(MIN_HEIGHT[FAIRE])}`,
      },
      LENGTH: {
        TOO_HIGH: `Length must be at most ${addThousandsSeparator(MAX_LENGTH[FAIRE])}`,
        TOO_LOW: `Length must be at least ${addThousandsSeparator(MIN_LENGTH[FAIRE])}`,
      },
      WEIGHT: {
        TOO_HIGH: `Weight must be at most ${addThousandsSeparator(MAX_WEIGHT[FAIRE])}`,
        TOO_LOW: `Weight must be at least ${addThousandsSeparator(MIN_WEIGHT[FAIRE])}`,
      },
      WIDTH: {
        TOO_HIGH: `Width must be at most ${addThousandsSeparator(MAX_WIDTH[FAIRE])}`,
        TOO_LOW: `Width must be at least ${addThousandsSeparator(MIN_WIDTH[FAIRE])}`,
      },
    },
    [SHOPIFY]: {
      WEIGHT: {
        TOO_HIGH: `Weight must be lower than ${addThousandsSeparator(MAX_WEIGHT[SHOPIFY])}`,
        TOO_LOW: 'Weight must be positive number',
      },
    },
  },
  SKU: {
    ALPHANUMERIC_BEGIN: 'SKU must begin with alphanumeric character',
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'SKU may not contain any of these characters: ^ $ `',
    },
    LENGTH_EXCEEDED: {
      [EBAY]: `SKU should not exceed ${MAX_SKU_LENGTH[EBAY]} characters`,
      [ETSY]: `SKU should not exceed ${MAX_SKU_LENGTH[ETSY]} characters`,
      [SHOPIFY]: `SKU should not exceed ${MAX_SKU_LENGTH[SHOPIFY]} characters`,
    },
    REQUIRED: 'Add SKU',
  },
  TAGS: {
    LENGTH_EXCEEDED: {
      [ETSY]: `Tag should not exceed ${MAX_TAG_LENGTH[ETSY]} characters`,
      [SHOPIFY]: `Tag should not exceed ${MAX_TAG_LENGTH[SHOPIFY]} characters`,
    },
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'Tag can only include spaces, letters, numbers, -, \', ™, © and ®',
    },
    ALPHANUMERIC_BEGIN: 'Tag must begin with alphanumeric character',
    ALPHANUMERIC_CONTAIN: 'Tag must contain alphanumeric character',
  },
  TITLE: {
    REQUIRED: 'Add title',
    ALPHANUMERIC_BEGIN: 'Title must begin with alphanumeric character',
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'Title can contain only alphanumeric, punctuation, ™©® characters and mathematical symbols',
    },
    LENGTH_EXCEEDED: {
      [EBAY]: `Title should not exceed ${MAX_TITLE_LENGTH[EBAY]} characters`,
      [ETSY]: `Title should not exceed ${MAX_TITLE_LENGTH[ETSY]} characters`,
      [FAIRE]: `Title should not exceed ${MAX_TITLE_LENGTH[FAIRE]} characters`,
      [SHOPIFY]: `Title should not exceed ${MAX_TITLE_LENGTH[SHOPIFY]} characters`,
    },
    CAPITALIZED_WORDS_NUMBER_EXCEEDED: 'Title can contain at most three words with all capitalized letters',
    SPECIAL_CHARACTERS_NUMBER_EXCEEDED: 'Title can contain characters %, : and & at most once',
  },
  VARIATIONS: {
    NOT_ALLOWED: 'Variations are not allowed for selected category',
  },
  VIDEOS: {
    FILES_TO_BIG: {
      [EBAY]: `Video file must be less than ${formatFileSize(MAX_VIDEO_SIZE[EBAY])}`,
      [ETSY]: `Video file must be less than ${formatFileSize(MAX_VIDEO_SIZE[ETSY])}`,
      [SHOPIFY]: `Video file must be less than ${formatFileSize(MAX_VIDEO_SIZE[SHOPIFY])}`,
    },
    INVALID_URL: 'Invalid URL',
    MAX_ALLOWED: {
      [EBAY]: `Videos must be at most ${MAX_NUMBER_OF_VIDEOS[EBAY]} per listing`,
      [ETSY]: `Videos must be at most ${MAX_NUMBER_OF_VIDEOS[ETSY]} per listing`,
      [SHOPIFY]: `Videos must be at most ${MAX_NUMBER_OF_VIDEOS[ETSY]} per listing`,
    },
    MAX_DURATION: {
      [EBAY]: `Video must be less than ${VIDEO_DURATION_MAX[EBAY]} seconds long`,
      [ETSY]: `Video must be less than ${VIDEO_DURATION_MAX[ETSY]} seconds long`,
      [SHOPIFY]: `Video must be at most ${VIDEO_DURATION_MAX[SHOPIFY]} seconds long`,
    },
    SMALL_DURATION: {
      [ETSY]: `Video must be at least ${VIDEO_DURATION_MINIMUM[ETSY]} seconds long`,
      [SHOPIFY]: `Video must be at least ${VIDEO_DURATION_MINIMUM[SHOPIFY]} seconds long`,
    },
    TOO_BIG: {
      [EBAY]: `Video must be at most ${addThousandsSeparator(VIDEO_DIMENSIONS_MAXIMUM[EBAY].HEIGHT)}p resolution`,
      [ETSY]: `Video must be at most ${addThousandsSeparator(VIDEO_DIMENSIONS_MAXIMUM[ETSY].HEIGHT)}p resolution`,
      [SHOPIFY]: `Video must be at most ${addThousandsSeparator(VIDEO_DIMENSIONS_MAXIMUM[SHOPIFY].HEIGHT)}p resolution`,
    },
    TOO_SMALL: {
      [EBAY]: `Video must be at least ${addThousandsSeparator(VIDEO_DIMENSIONS_MINIMUM[EBAY])}p resolution`,
      [ETSY]: `Video must be at least ${addThousandsSeparator(VIDEO_DIMENSIONS_MINIMUM[ETSY])}p resolution`,
      [SHOPIFY]: `Video must be at least ${addThousandsSeparator(VIDEO_DIMENSIONS_MINIMUM[SHOPIFY])}p resolution`,
    },
    UNKNOWN: 'Video file is corrupted or unsupported',
    UNSUPPORTED_FORMAT: {
      [EBAY]: `File must be one of the following types: ${VIDEO_FILE_FORMATS[EBAY].reduce(reduceFileFormats, '')}`,
      [ETSY]: `File must be one of the following types: ${VIDEO_FILE_FORMATS[ETSY].reduce(reduceFileFormats, '')}`,
      [SHOPIFY]: `File must be one of the following types: ${VIDEO_FILE_FORMATS[SHOPIFY].reduce(reduceFileFormats, '')}`,
    },
    UNSUPPORTED_URL: 'Only Youtube and Vimeo videos supported',
  },
};
